import React, { useMemo } from 'react'
import * as graph from '@adc/sdk'
import { CurrentTargetAudience, Listing } from '@adc/sdk/types'
import { utils, LabelValue } from '@adc/ui-core'
import styles from './styles.scss'
import classnames from 'classnames/bind'
import { formatOfferDate } from '../../../util/formatters'
import moment from 'moment'

const { enums } = graph

const cx = classnames.bind(styles)

const baseElmId = 'auction-details'

type AuctionDetailsProps = {
  listing: Partial<Listing>
  viewingFrom?: CurrentTargetAudience
}
const AuctionDetails = ({
  listing,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
}: AuctionDetailsProps) => {
  const formattedStartDate = formatOfferDate(listing?.auction?.start_date)
  const formattedEndDate = formatOfferDate(listing?.auction?.end_date)
  const reviewDate = formatOfferDate(
    listing?.online_auction_segment?.configuration?.review_date
  )
  const secondsOnAuction = moment().diff(
    listing?.auction?.start_date,
    'seconds'
  )
  // Minimum count of days will always be '1 day'
  const formattedDaysOnAuction =
    secondsOnAuction < 172800 // less than 2 days (48 hours)
      ? '1 day'
      : (utils.countdown.formatPeriodTime(
          secondsOnAuction,
          viewingFrom === graph.enums.CurrentTargetAudience.Seller
            ? 'days'
            : 'weeks',
          'days'
        ) as string)

  const segmentType =
    listing?.online_auction_segment?.segment_type ||
    (listing?.selling_method?.__typename === 'OnlineAuctionSegment'
      ? listing?.selling_method?.segment_type
      : undefined)

  const isEnglishType =
    segmentType === graph.enums.OnlineAuctionSegmentType.English

  const isSealedBidAndBuyer =
    segmentType === graph.enums.OnlineAuctionSegmentType.SealedBid &&
    viewingFrom === graph.enums.CurrentTargetAudience.Buyer

  const isPrivateSeller =
    listing?.listing_configuration?.product_type ===
    enums.ProductType.PrivateSeller

  const displayAsListPrice =
    (isPrivateSeller && !isEnglishType) || isSealedBidAndBuyer

  const reserveAmount =
    listing?.online_auction_segment?.current_reserve_amount ??
    listing?.auction?.reserve_price ??
    0

  const priceValue = isEnglishType
    ? reserveAmount
    : listing?.valuation?.seller_current_value_amount ?? 0

  const priceLabel = isEnglishType
    ? 'Reserve Price'
    : `${displayAsListPrice ? 'List' : 'Your Listing'} Price${
        isSealedBidAndBuyer ? ':' : ''
      }`

  return useMemo(
    () => (
      <div
        className={cx(
          'details-container',
          isSealedBidAndBuyer ? 'sealed-bid' : ''
        )}
      >
        {viewingFrom === graph.enums.CurrentTargetAudience.Seller && (
          <div className={cx('detail-row')}>
            <div className={cx('detail')}>
              <LabelValue
                elmId={`${baseElmId}-views`}
                value={listing?.primary_property?.metrics?.pdp_view_count || 0}
                label='Views'
                level={4}
              />
            </div>
            <div className={cx('detail')}>
              <LabelValue
                elmId={`${baseElmId}-saves`}
                value={listing?.primary_property?.metrics?.save_count || 0}
                label='Saves'
                level={4}
              />
            </div>
          </div>
        )}
        <div className={cx('detail-row')}>
          <div className={cx('detail')}>
            <LabelValue
              elmId={`${baseElmId}-listing-value`}
              value={priceValue}
              label={priceLabel}
              level={4}
              isMoneyValue
            />
          </div>
        </div>
        {!isSealedBidAndBuyer && (
          <>
            <div className={cx('detail-row')}>
              <div className={cx('detail')}>
                <LabelValue
                  elmId={`${baseElmId}-listing-start-date`}
                  value={formattedStartDate}
                  label='Listing Start Date'
                  level={4}
                />
              </div>
              <div className={cx('detail')}>
                <LabelValue
                  elmId={`${baseElmId}-listing-end-date`}
                  value={formattedEndDate}
                  label='Listing End Date'
                  level={4}
                />
              </div>
            </div>
            <div className={cx('detail-row')}>
              {reviewDate && (
                <LabelValue
                  elmId={`${baseElmId}-reviewing-offer-date`}
                  value={reviewDate}
                  label='Reviewing Offer Date'
                  level={4}
                />
              )}
              <div className={cx('detail')}>
                <LabelValue
                  elmId={`${baseElmId}-listing-days_on_auction`}
                  value={formattedDaysOnAuction}
                  label='Days on Market'
                  level={4}
                />
              </div>
            </div>
          </>
        )}
      </div>
    ),
    [listing, viewingFrom]
  )
}

export default AuctionDetails
